import React from 'react';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { AgTechGridProps, gridItems } from '@agtech/web/components/Grids/Data/AgTechGridProps';
import { AgTechActionButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { useAgTechQuery } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';
import { OperationDataGetUsersQuery } from '@sales/data/operation/users/OperationUserQueries';
import { OperationDataGetRoleAssignmentsQuery } from '@sales/data/operation/roles/OperationRoleQueries';
import TeamMemberActionMenu from './TeamMemberActionMenu';

type TeamMember = {
    id: number,
    userName: string,
    firstName: string,
    lastName: string,
    roleIds: number[],
    roleNames: string[]
}

const TeamRolesTab = () => {
    const usersQuery = useAgTechQuery({ query: OperationDataGetUsersQuery });
    const assignmentsQuery = useAgTechQuery({ query: OperationDataGetRoleAssignmentsQuery });

    return (
        <AgTechQueryLoadingSurface query={usersQuery}>
            {(users) => (
                <AgTechQueryLoadingSurface query={assignmentsQuery}>
                    {(assignments) => {
                        const members: TeamMember[] = Object.values(users).map(user => {
                            const assignment = assignments[user.id];

                            return {
                                id: user.id,
                                userName: user.userName,
                                firstName: user.firstName,
                                lastName: user.lastName,
                                roleIds: assignment?.roleIds ?? [],
                                roleNames: assignment?.roleNames ?? []
                            };
                        });

                        const teamGridProps: AgTechGridProps<TeamMember> = {
                            header: {
                                title: 'Team'
                            },
                            data: {
                                itemQuery: gridItems(members)
                            },
                            searchConfig: {
                                options: [
                                    { field: 'User Name', value: m => m.userName },
                                    { field: 'Name', value: m => m.firstName + ' ' + m.lastName },
                                ]
                            },
                            columns: [
                                {
                                    header: 'User Name',
                                    value: m => m.userName
                                },
                                {
                                    header: 'Name',
                                    value: m => m.firstName + ' ' + m.lastName
                                },
                                {
                                    header: 'Roles',
                                    value: m => m.roleNames.length > 0 ? m.roleNames.join(', ') : 'None'
                                },
                            ],
                            isRowEntityTheSame: (prev, curr) =>
                                prev.id === curr.id && prev.roleNames.join(',') === curr.roleNames.join(','),
                            actions: {
                                rendering: member => (
                                    <AgTechActionButton>
                                        <TeamMemberActionMenu
                                            userId={member.id}
                                            userName={member.userName}
                                            roleIds={member.roleIds}
                                        />
                                    </AgTechActionButton>
                                )
                            }
                        };

                        return <AgTechGrid {...teamGridProps} />;
                    }}
                </AgTechQueryLoadingSurface>
            )}
        </AgTechQueryLoadingSurface>
    );
};

export default TeamRolesTab;
